import { startTransition } from 'react'
import { useCompany } from '@/hooks/use-companies'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { CompanySidePeekHeader } from './company-side-peek-header'
import { FinancialSnapshot } from './financial-snapshot'
import { InvestmentSection } from './investment-section'
import { GovernanceSection } from './governance-section'
import { ContactsSection } from './contacts-section'
import { Skeleton } from '@/components/ui/skeleton'

interface CompanySidePeekProps {
  companyId: string
  /** When set, status badge becomes fund-scoped (CompanyFund) instead of company-scoped. */
  fundContextId?: string | null
  onClose: () => void
  onExpand?: () => void
}

export function CompanySidePeek({
  companyId,
  fundContextId,
  onClose,
  onExpand,
}: CompanySidePeekProps) {
  const { data: company, isLoading, error, refetch } = useCompany(companyId)

  const handleClose = () => {
    startTransition(() => onClose())
  }

  return (
    <Sheet open onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        hideClose
        className="w-120 overflow-y-auto bg-sidebar p-0 sm:max-w-none"
      >
        {/* Accessible title for screen readers */}
        <SheetTitle className="sr-only">Company Details</SheetTitle>
        <SheetDescription className="sr-only">
          Detailed information about the selected company
        </SheetDescription>

        <div className="space-y-3 px-3 pb-6 pt-4">
          {isLoading && <CompanySidePeekSkeleton />}

          {error && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Typography as="p" className="text-destructive">
                Failed to load company details
              </Typography>
              <Button onClick={() => refetch()}>Retry</Button>
            </div>
          )}

          {company && (
            <>
              <CompanySidePeekHeader
                company={company}
                fundContextId={fundContextId}
                onClose={handleClose}
                onExpand={onExpand}
              />
              <FinancialSnapshot company={company} />
              <InvestmentSection company={company} />
              <GovernanceSection company={company} onUpdate={refetch} />
              <ContactsSection company={company} onUpdate={refetch} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function CompanySidePeekSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-16 w-full" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
      </div>
    </div>
  )
}
